import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "compliance" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative border-r border-border grid-texture">
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="h-7 w-7 bg-primary flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-white" /></div>
            <span className="font-heading text-lg font-extrabold tracking-tighter">VAULTVEC</span>
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tighter leading-tight">Provision your role-based access to the protection console.</h2>
            <p className="mt-4 text-muted-foreground font-mono text-xs tracking-widest">SEGREGATED DUTIES · FULL TRACEABILITY</p>
          </div>
          <div />
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Create account</h1>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs text-muted-foreground">Full name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-secondary/40 border-border mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-secondary/40 border-border mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Password</Label>
              <Input data-testid="register-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none bg-secondary/40 border-border mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="register-role" className="rounded-none bg-secondary/40 border-border mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">IT Admin</SelectItem>
                  <SelectItem value="compliance">Compliance Officer</SelectItem>
                  <SelectItem value="auditor">Auditor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p data-testid="register-error" className="text-sm text-primary font-mono">{error}</p>}
            <Button data-testid="register-submit" disabled={busy} className="w-full rounded-none bg-primary hover:bg-primary/90 text-white h-11">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Already registered? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
